import React, { Fragment, useCallback, useEffect, useState } from "react";
import "./index.less";
import { IServiceVideoChat, IServiceVideoChatDevice } from "@obsbot/remo-services";
import { SettingSelector } from "../../SettingPage/SettingSelector";
import { useSafePromise } from "../../../utils/hooks";
import { useTranslate } from "@obsbot/remo-i18n";
import { SettingSlider } from "../../SettingPage/SettingSlider";
import { BottomToolSettingIcon } from "../../SvgIconCom";

export interface SettingProps {
    rtc: IServiceVideoChat;
    rightMenuType: string;
    toSetting: (toVideo?: boolean) => void;
}
export const BottomToolsSetting: React.FC<SettingProps> = ({ rtc, rightMenuType, toSetting }) => {
    const t = useTranslate();
    const [currentDeviceID, setCurrentDeviceID] = useState<string | null>(null);
    const [camDeviceList, setCamDeviceList] = useState<IServiceVideoChatDevice[]>([]);
    // 麦克风设备
    const [micDevices, setMicDevices] = useState<IServiceVideoChatDevice[]>([]);
    // 当前的麦克风设备ID
    const [currentMicDeviceID, setCurrentMicDeviceID] = useState<string | null>(null);
    // 当前的麦克风音量
    const [currentMicVolume, setCurrentMicVolume] = useState<number>(rtc.getMicVolume());
    // 当前的扬声器音量
    const [currentSpeakerVolume, setCurrentSpeakerVolume] = useState<number>(
        rtc.getSpeakerVolume(),
    );
    // 扬声器设备
    const [speakerDevices, setSpeakerDevices] = useState<IServiceVideoChatDevice[]>([]);
    // 当前的扬声器设备ID
    const [currentSpeakerDeviceID, setCurrentSpeakerDeviceID] = useState<string | null>(null);
    const sp = useSafePromise();

    // 获取相机设备列表
    useEffect(() => {
        const updateCameraDevices = async (deviceID?: string): Promise<void> => {
            if (!rtc) {
                return;
            }
            const devices = await sp(rtc.getCameraDevices());
            if (devices.length === 0) {
                setCurrentDeviceID(null);
                return;
            }
            setCamDeviceList(devices);
            const camId = deviceID || rtc.getCameraID() || devices[0]?.deviceId;
            setCurrentDeviceID(camId || null);
            if (devices.every(device => device.deviceId !== camId)) {
                setCurrentDeviceID(null);
            }
            if (camId) {
                onCameraChanged(camId);
            }
        };
        updateCameraDevices();
        const updateMicDevices = async (deviceID?: string): Promise<void> => {
            const devices = await sp(rtc.getMicDevices());
            setMicDevices(devices);
            if (devices.length === 0) {
                setCurrentMicDeviceID(null);
                return;
            }
            const micId = deviceID || rtc.getMicID() || devices[0]?.deviceId;
            setCurrentMicDeviceID(micId || null);
            if (devices.every(device => device.deviceId !== micId)) {
                setCurrentMicDeviceID(null);
            }
            if (micId) {
                onMicChanged(micId);
            }
        };
        updateMicDevices();
        const updateSpeakerDevices = async (deviceID?: string): Promise<void> => {
            const devices = await sp(rtc.getSpeakerDevices());
            setSpeakerDevices(devices);
            if (devices.length === 0) {
                setCurrentSpeakerDeviceID(null);
                return;
            }
            const speakerId = deviceID || rtc.getSpeakerID() || devices[0]?.deviceId;
            setCurrentSpeakerDeviceID(speakerId || null);
            if (devices.every(device => device.deviceId !== speakerId)) {
                setCurrentSpeakerDeviceID(null);
            }
            if (speakerId) {
                onSpeakerChanged(speakerId);
            }
        };
        updateSpeakerDevices();
        rtc.events.on("camera-changed", updateCameraDevices);
        rtc.events.on("mic-changed", updateMicDevices);
        rtc.events.on("speaker-changed", updateSpeakerDevices);
        return () => {
            rtc.events.off("camera-changed", updateCameraDevices);
            rtc.events.off("mic-changed", updateMicDevices);
            rtc.events.off("speaker-changed", updateSpeakerDevices);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rtc, sp]);

    // 处理选择相机
    const onCameraChanged = useCallback(
        (deviceID: string): void => {
            rtc?.setCameraID(deviceID);
        },
        [rtc],
    );
    // 处理设置麦克风音量
    const onSetMicVolume = useCallback(
        (volume: number): void => {
            rtc.setCaptureVolume(volume);
            setCurrentMicVolume(volume);
        },
        [rtc],
    );
    // 监听选择麦克风设备
    const onMicChanged = useCallback(
        (deviceID: string): void => {
            rtc.setMicID(deviceID);
        },
        [rtc],
    );
    // 监听选择扬声器设备
    const onSpeakerChanged = useCallback(
        (deviceID: string): void => {
            rtc.setSpeakerID(deviceID);
        },
        [rtc],
    );
    // 处理设置扬声器音量
    const onSetSpeakerVolume = useCallback(
        (volume: number): void => {
            rtc.setPlayoutVolume(volume);
            setCurrentSpeakerVolume(volume);
        },
        [rtc],
    );

    useEffect(() => {
        const min = 0;
        const max = 100;
        const step = 1;
        if (rightMenuType === "camera") {
            return;
        }
        const handleMicScroll = (event: any): void => {
            // 阻止默认的页面滚动行为
            event.preventDefault();
            let newValue = currentMicVolume;
            // event.deltaY > 0 表示向下滚动，滑块应该向右，值变小
            if (event.deltaY > 0) {
                newValue = Math.max(min, currentMicVolume - step);
            } else {
                // 向上滚动，值变小
                newValue = Math.min(max, currentMicVolume + step);
            }
            setCurrentMicVolume(newValue);
        };

        const handleSpeakerScroll = (event: any): void => {
            // 阻止默认的页面滚动行为
            event.preventDefault();
            let newValue = currentSpeakerVolume;
            // event.deltaY > 0 表示向下滚动，滑块应该向右，值变小
            if (event.deltaY > 0) {
                newValue = Math.max(min, currentSpeakerVolume - step);
            } else {
                // 向上滚动，值变小
                newValue = Math.min(max, currentSpeakerVolume + step);
            }
            setCurrentSpeakerVolume(newValue);
        };

        // 找到你需要监听滚轮事件的 DOM 元素，例如整个滑块容器
        const micSliderContainer = document.getElementsByClassName("mic-volume-slider")[0];
        const speakerSliderContainer = document.getElementsByClassName("speaker-volume-slider")[0];
        if (micSliderContainer) {
            micSliderContainer.addEventListener("wheel", handleMicScroll);
        }
        if (speakerSliderContainer) {
            speakerSliderContainer.addEventListener("wheel", handleSpeakerScroll);
        }

        // 组件卸载时移除事件监听器
        return () => {
            if (micSliderContainer) {
                micSliderContainer.removeEventListener("wheel", handleMicScroll);
            }
            if (speakerSliderContainer) {
                speakerSliderContainer.removeEventListener("wheel", handleSpeakerScroll);
            }
        };
    }, [currentSpeakerVolume, currentMicVolume, rightMenuType]);

    return (
        <div className="right-menu-content">
            <div className="content-item setting-camera route-to-setting">
                <div
                    className="content-item-container"
                    onClick={() => toSetting(rightMenuType === "camera" ? true : false)}
                >
                    <BottomToolSettingIcon color="#FFFFFFB3" />
                    <div className="content-item-title">
                        {t(
                            rightMenuType === "camera"
                                ? "settings.rtc-setting.videoSetting"
                                : "settings.rtc-setting.audioSetting",
                        )}
                    </div>
                </div>
            </div>
            {rightMenuType === "camera" && (
                <div className="content-item setting-camera">
                    <div className="content-item-title">{t("previewCam.camDevice")}</div>
                    <SettingSelector
                        key={currentDeviceID}
                        className="rtc-setting-selector"
                        defaultValue={currentDeviceID}
                        options={camDeviceList.map(device => ({
                            label: device.label,
                            value: device.deviceId,
                            title: "",
                        }))}
                        rootClassName=""
                        onChange={onCameraChanged}
                    />
                </div>
            )}
            {rightMenuType === "microphone" && (
                <Fragment>
                    <div className="content-item setting-microphone">
                        <div className="content-item-title">
                            {t("settings.rtc-setting.inputDevice")}
                        </div>
                        <SettingSelector
                            key={currentMicDeviceID}
                            className="rtc-setting-selector"
                            defaultValue={currentMicDeviceID}
                            options={micDevices.map(device => ({
                                label: device.label,
                                value: device.deviceId,
                                title: "",
                            }))}
                            onChange={onMicChanged}
                        />
                    </div>
                    <div className="content-item setting-microphone mic-volume-slider">
                        <div className="content-item-title">
                            {t("settings.rtc-setting.micInputVolume")}
                        </div>
                        <SettingSlider
                            bg_handle="#5333D5"
                            bg_track="linear-gradient(90deg, #744CF6 0%, #5333D5 100%)"
                            className="rtc-setting-page-slider"
                            value={currentMicVolume}
                            onChange={onSetMicVolume}
                        />
                    </div>
                </Fragment>
            )}
            {rightMenuType === "headphones" && (
                <Fragment>
                    <div className="content-item setting-headphones">
                        <div className="content-item-title">
                            {t("settings.rtc-setting.outputDevice")}
                        </div>
                        <SettingSelector
                            key={currentSpeakerDeviceID}
                            className="rtc-setting-selector"
                            defaultValue={currentSpeakerDeviceID}
                            options={speakerDevices.map(device => ({
                                label: device.label,
                                value: device.deviceId,
                                title: "",
                            }))}
                            onChange={onSpeakerChanged}
                        />
                    </div>
                    <div className="content-item setting-headphones speaker-volume-slider">
                        <div className="content-item-title">
                            {t("settings.rtc-setting.voiceOutputVolume")}
                        </div>
                        <SettingSlider
                            bg_handle="#5333D5"
                            bg_track="linear-gradient(90deg, #744CF6 0%, #5333D5 100%)"
                            className="rtc-setting-page-slider"
                            value={currentSpeakerVolume}
                            onChange={onSetSpeakerVolume}
                        />
                    </div>
                </Fragment>
            )}
        </div>
    );
};
